import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { 
  ChevronRight, 
  ArrowLeft, 
  Home, 
  Car, 
  Search, 
  PlusCircle, 
  MessageCircle, 
  User, 
  Building, 
  LandPlot, 
  Hotel,
  Zap,
  Truck,
  Ship,
  Bike,
  Image as ImageIcon,
  Clock,
  X,
  SlidersHorizontal,
  Eye,
  Heart
} from 'lucide-react';
import './App.css';

function isAbortError(err) {
  return (
    err &&
    (err.code === 'ERR_CANCELED' ||
      err.name === 'CanceledError' ||
      (typeof axios.isCancel === 'function' && axios.isCancel(err)))
  );
}

/** API bazen HTML/bozuk gövde döndürebilir; kök liste boş kalmasın diye güvenli ayrıştırma */
function parseCategoryApiPayload(data) {
  if (Array.isArray(data)) return { ok: true, rows: data };
  if (data && typeof data === 'object' && data.error) {
    return { ok: false, error: data.error };
  }
  if (typeof data === 'string') {
    const t = data.trim();
    if (!t) return { ok: false, error: 'Boş yanıt' };
    try {
      const p = JSON.parse(t);
      if (Array.isArray(p)) return { ok: true, rows: p };
      if (p && p.error) return { ok: false, error: p.error };
    } catch (e) {
      return { ok: false, error: 'JSON ayrıştırılamadı' };
    }
  }
  return { ok: false, error: 'Beklenmeyen yanıt tipi' };
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentLayer, setCurrentLayer] = useState([]);
  const [listings, setListings] = useState([]);
  const [latestListings, setLatestListings] = useState([]);
  const [viewMode, setViewMode] = useState('vitrin'); // Default: 'vitrin' (Vitrin)
  const [history, setHistory] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCategoryCount, setSelectedCategoryCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [missingBrandLogos, setMissingBrandLogos] = useState({});

  /** Android / tarayıcı geri: pushState ile eşleşen derinlik */
  const navStackDepthRef = useRef(0);
  const [navStackDepth, setNavStackDepth] = useState(0);
  const suppressPopstateRef = useRef(false);
  const handleBackRef = useRef(() => {});
  const preSearchRef = useRef(null);
  const categoryFetchCtrlRef = useRef(null);
  const listingsFetchCtrlRef = useRef(null);
  const categoryLayerCacheRef = useRef(new Map());
  const prefetchQueueRef = useRef([]);
  const prefetchActiveRef = useRef(0);
  const prefetchSingleInflightRef = useRef(new Set());

  const CATEGORY_CACHE_TTL_MS = 120000;
  const MAX_CATEGORY_PREFETCH = 6;

  const [categoriesBusy, setCategoriesBusy] = useState(false);
  const [listingsBusy, setListingsBusy] = useState(false);
  const [listingsRenderKey, setListingsRenderKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const pUser = axios
      .get('api/get_user_info.php')
      .then((res) => {
        if (cancelled) return;
        if (res.data.logged_in) setUser(res.data.user);
      })
      .catch(() => {});

    const pLatest = axios
      .get('api/get_listings.php?limit=10')
      .then((res) => {
        if (cancelled) return;
        setLatestListings(res.data);
      })
      .catch((err) => console.error('Latest listings error:', err));

    Promise.allSettled([pUser, pLatest]).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Okunmamış mesaj kontrolü
  useEffect(() => {
    if (!user) return;

    const checkUnreadMessages = () => {
      axios.get('api/get_unread_messages_count.php')
        .then(res => {
          if (res.data.unread_count !== undefined) {
            setHasUnreadMessages(res.data.unread_count > 0);
          }
        })
        .catch(err => console.error("Unread messages check error:", err));
    };

    // İlk kontrol
    checkUnreadMessages();

    // Sunucu yükünü azaltmak için daha seyrek kontrol
    const interval = setInterval(checkUnreadMessages, 15000);

    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const onPopState = () => {
      if (suppressPopstateRef.current) {
        suppressPopstateRef.current = false;
        return;
      }
      if (navStackDepthRef.current <= 0) {
        return;
      }
      navStackDepthRef.current -= 1;
      setNavStackDepth(navStackDepthRef.current);
      handleBackRef.current();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    return () => {
      categoryFetchCtrlRef.current?.abort();
      listingsFetchCtrlRef.current?.abort();
    };
  }, []);

  const pushNavHistory = () => {
    window.history.pushState({ kktc: true }, '', '');
    navStackDepthRef.current += 1;
    setNavStackDepth(navStackDepthRef.current);
  };

  const runListingsRequest = (url, viewModeAfter, { onFailure } = {}) => {
    listingsFetchCtrlRef.current?.abort();
    const ctrl = new AbortController();
    listingsFetchCtrlRef.current = ctrl;
    setListingsBusy(true);
    axios
      .get(url, { signal: ctrl.signal })
      .then((res) => {
        if (listingsFetchCtrlRef.current !== ctrl) return;
        setListings(res.data);
        setViewMode(viewModeAfter);
        pushNavHistory();
        setListingsRenderKey((k) => k + 1);
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        if (viewModeAfter === 'search') {
          console.error('Search error:', err);
        } else {
          console.error('Listings fetch error:', err);
        }
        onFailure?.();
      })
      .finally(() => {
        if (listingsFetchCtrlRef.current === ctrl) {
          setListingsBusy(false);
        }
      });
  };

  const fetchListings = (catId) => {
    runListingsRequest(`api/get_listings.php?category_id=${catId}`, 'listings');
  };

  const drainCategoryPrefetch = () => {
    const cache = categoryLayerCacheRef.current;
    const q = prefetchQueueRef.current;
    while (prefetchActiveRef.current < MAX_CATEGORY_PREFETCH && q.length > 0) {
      const pid = q.shift();
      const k = String(pid);
      if (cache.has(k)) continue;
      prefetchActiveRef.current += 1;
      axios
        .get(`api/get_categories.php?parent_id=${pid}`)
        .then((res) => {
          const parsed = parseCategoryApiPayload(res.data);
          if (parsed.ok && parsed.rows.length > 0) {
            cache.set(k, { data: parsed.rows, ts: Date.now() });
          }
        })
        .catch(() => {})
        .finally(() => {
          prefetchActiveRef.current -= 1;
          drainCategoryPrefetch();
        });
    }
  };

  const scheduleCategoryPrefetches = (layerRows) => {
    if (!layerRows?.length) return;
    const cache = categoryLayerCacheRef.current;
    const q = prefetchQueueRef.current;
    for (const cat of layerRows) {
      const k = String(cat.id);
      if (cache.has(k)) continue;
      if (q.includes(cat.id)) continue;
      q.push(cat.id);
    }
    drainCategoryPrefetch();
  };

  const prefetchCategoryLayerOnHover = (catId) => {
    const k = String(catId);
    const cache = categoryLayerCacheRef.current;
    if (cache.has(k) || prefetchSingleInflightRef.current.has(k)) return;
    prefetchSingleInflightRef.current.add(k);
    axios
      .get(`api/get_categories.php?parent_id=${catId}`)
      .then((res) => {
        const parsed = parseCategoryApiPayload(res.data);
        if (parsed.ok && parsed.rows.length > 0) {
          cache.set(k, { data: parsed.rows, ts: Date.now() });
        }
      })
      .catch(() => {})
      .finally(() => {
        prefetchSingleInflightRef.current.delete(k);
      });
  };

  const fetchCategories = (parentId) => {
    categoryFetchCtrlRef.current?.abort();
    const ctrl = new AbortController();
    categoryFetchCtrlRef.current = ctrl;
    const key = parentId == null || parentId === '' ? '' : String(parentId);
    const cache = categoryLayerCacheRef.current;
    const cached = cache.get(key);
    const cacheFresh =
      cached &&
      Array.isArray(cached.data) &&
      cached.data.length > 0 &&
      Date.now() - cached.ts < CATEGORY_CACHE_TTL_MS;

    if (cacheFresh) {
      setCurrentLayer(cached.data);
      setViewMode((vm) => {
        if (vm === 'listings' || vm === 'search') return vm;
        return 'categories';
      });
      if (parentId) {
        pushNavHistory();
      }
      scheduleCategoryPrefetches(cached.data);
      setCategoriesBusy(false);
      axios
        .get(`api/get_categories.php?parent_id=${parentId || ''}`, { signal: ctrl.signal })
        .then((res) => {
          if (categoryFetchCtrlRef.current !== ctrl) return;
          const parsed = parseCategoryApiPayload(res.data);
          if (!parsed.ok) {
            console.error('get_categories:', parsed.error, res.data);
            return;
          }
          const rows = parsed.rows;
          if (rows.length > 0) {
            cache.set(key, { data: rows, ts: Date.now() });
            setCurrentLayer(rows);
            scheduleCategoryPrefetches(rows);
          } else if (parentId) {
            fetchListings(parentId);
          }
        })
        .catch((err) => {
          if (!isAbortError(err)) {
            console.error('Categories fetch error:', err);
          }
        });
      return;
    }

    setCategoriesBusy(true);
    axios
      .get(`api/get_categories.php?parent_id=${parentId || ''}`, { signal: ctrl.signal })
      .then((res) => {
        if (categoryFetchCtrlRef.current !== ctrl) return;
        const parsed = parseCategoryApiPayload(res.data);
        if (!parsed.ok) {
          console.error('get_categories:', parsed.error, res.data);
          return;
        }
        const rows = parsed.rows;
        if (rows.length > 0) {
          cache.set(key, { data: rows, ts: Date.now() });
          setCurrentLayer(rows);
          setViewMode((vm) => {
            if (vm === 'listings' || vm === 'search') return vm;
            return 'categories';
          });
          if (parentId) {
            pushNavHistory();
          }
          scheduleCategoryPrefetches(rows);
        } else if (parentId) {
          fetchListings(parentId);
        }
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        console.error('Categories fetch error:', err);
      })
      .finally(() => {
        if (categoryFetchCtrlRef.current === ctrl) {
          setCategoriesBusy(false);
        }
      });
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    if (!searchQuery.trim()) return;

    preSearchRef.current = {
      viewMode,
      history: history.map((h) => ({
        ...h,
        data: Array.isArray(h.data) ? [...h.data] : h.data,
      })),
      currentLayer: [...currentLayer],
      selectedCategory,
      selectedCategoryCount,
      listings: [...listings],
    };

    runListingsRequest(
      `api/search_listings.php?q=${encodeURIComponent(searchQuery)}`,
      'search',
      { onFailure: () => { preSearchRef.current = null; } }
    );
  };

  const handleCategoryClick = (cat) => {
    setHistory([...history, { mode: viewMode, data: currentLayer, cat: selectedCategory, count: selectedCategoryCount }]);
    setSelectedCategory(cat);
    setSelectedCategoryCount(cat.count ?? 0);
    fetchCategories(cat.id);
  };

  const handleBack = () => {
    const lastState = history[history.length - 1];
    if (lastState) {
      setViewMode(lastState.mode);
      setCurrentLayer(lastState.data);
      setSelectedCategory(lastState.cat);
      setSelectedCategoryCount(lastState.count ?? 0);
      setHistory(history.slice(0, -1));
    } else if (viewMode === 'search' && preSearchRef.current) {
      const s = preSearchRef.current;
      setViewMode(s.viewMode);
      setHistory(s.history);
      setCurrentLayer(s.currentLayer);
      setSelectedCategory(s.selectedCategory);
      setSelectedCategoryCount(s.selectedCategoryCount ?? 0);
      setListings(s.listings);
      preSearchRef.current = null;
    } else if (viewMode === 'categories') {
      setViewMode('vitrin');
      setSelectedCategory(null);
      setSelectedCategoryCount(0);
    } else {
      setViewMode('categories');
      fetchCategories(null);
      setSelectedCategory(null);
      setSelectedCategoryCount(0);
    }
  };

  handleBackRef.current = handleBack;

  // Filtreleme yolundaki kategoriler (hem kategori hem ilan listesi görünümünde kullanılır)
  const pathCategories = useMemo(
    () => [...(history || []).map((h) => h.cat).filter(Boolean), selectedCategory].filter(Boolean),
    [history, selectedCategory]
  );

  const categoryLayerScrollKey = useMemo(
    () => currentLayer.map((c) => c.id).join('-'),
    [currentLayer]
  );

  /** Alt kategoriye girince liste üstten başlasın (önceki kaydırma konumu kalmasın) */
  useEffect(() => {
    if (viewMode !== 'categories') return;
    const run = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    run();
    const tid = window.setTimeout(run, 0);
    return () => clearTimeout(tid);
  }, [viewMode, categoryLayerScrollKey]);

  const normalizeBrandForFile = (name) => {
    const normalized = (name || '')
      .toLowerCase()
      .replace(/ı/g, 'i')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/['’`]/g, '')
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const brandAliases = {
      cherry: 'chery',
      suziki: 'suzuki'
    };

    return brandAliases[normalized] || normalized;
  };

  /** Vasıta > Otomobil veya Arazi dalında mıyız (kategori listesi)? */
  const isVehicleOtomobilOrAraziLogoContext = () => {
    if (viewMode !== 'categories') return false;
    const names = pathCategories.map((c) => (c?.name || '').toLowerCase());
    const vasIdx = names.findIndex((n) => n.includes('vasıta') || n.includes('vasita'));
    if (vasIdx === -1) return false;
    return names.slice(vasIdx + 1).some((n) => n.includes('otomobil') || n.includes('arazi'));
  };

  /**
   * Otomobil/Arazi altında markadan sonraki seviyelerde logo dosyası bu isimden türetilir (ör. hep BMW).
   * Marka listesinde (Otomobil’in doğrudan çocukları) null döner → her satır kendi adıyla logo alır.
   */
  const getVehicleBrandLogoSourceName = () => {
    const pc = pathCategories;
    const names = pc.map((c) => (c?.name || '').toLowerCase());
    const vasIdx = names.findIndex((n) => n.includes('vasıta') || n.includes('vasita'));
    if (vasIdx === -1) return null;
    const otIdx = names.findIndex(
      (n, i) => i > vasIdx && (n.includes('otomobil') || n.includes('arazi'))
    );
    if (otIdx === -1) return null;
    if (pc.length <= otIdx + 1) return null;
    return pc[otIdx + 1]?.name || null;
  };

  const getIcon = (name) => {
    const n = name.toLowerCase();
    if (n === 'emlak') return <Home size={22} />;
    if (n === 'vasıta') return <Car size={22} />;
    if (n.includes('konut')) return <Building size={22} />;
    if (n.includes('arsa')) return <LandPlot size={22} />;
    if (n.includes('turistik')) return <Hotel size={22} />;
    if (n.includes('elektrikli')) return <Zap size={22} />;
    if (n.includes('ticari')) return <Truck size={22} />;
    if (n.includes('deniz')) return <Ship size={22} />;
    if (n.includes('motosiklet')) return <Bike size={22} />;
    return <ChevronRight size={18} />;
  };

  const renderCategoryLeftIcon = (catName) => {
    if (!isVehicleOtomobilOrAraziLogoContext()) {
      return getIcon(catName);
    }

    const inheritedBrand = getVehicleBrandLogoSourceName();
    const logoSourceName = inheritedBrand || catName;
    const fileKey = normalizeBrandForFile(logoSourceName);
    if (!fileKey || missingBrandLogos[fileKey]) {
      return getIcon(catName);
    }

    return (
      <img
        src={`logolar/${fileKey}.png`}
        alt={catName}
        className="brand-logo-icon"
        loading="lazy"
        onError={() => {
          setMissingBrandLogos((prev) => ({ ...prev, [fileKey]: true }));
        }}
      />
    );
  };

  if (loading) return (
    <div className="loading">
      <Zap size={40} className="animate-pulse" />
      <p>Yükleniyor...</p>
    </div>
  );

  return (
    <div className="app-container">
      {(categoriesBusy || listingsBusy) && (
        <div className="nav-loading-strip" role="progressbar" aria-busy="true" aria-label="Yükleniyor" />
      )}
      <header className="mobile-header">
        {(history.length > 0 || viewMode === 'listings' || viewMode === 'search' || (viewMode === 'categories' && navStackDepth > 0)) ? (
          <button type="button" className="back-btn" onClick={() => window.history.back()}>
            <ArrowLeft size={20} />
          </button>
        ) : <div style={{width: 36}}></div>}
        <h1>{viewMode === 'listings' ? selectedCategory?.name : (viewMode === 'search' ? 'Arama Sonuçları' : (viewMode === 'vitrin' ? 'VITRIN' : 'KATEGORİLER'))}</h1>
        <div className="user-menu">
          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {user.role === 'admin' && (
                <a href="admin.php" title="Admin Paneli" style={{ color: '#007bff', textDecoration: 'none', marginRight: '5px' }}>
                  <SlidersHorizontal size={18} />
                </a>
              )}
              <a href="panel.php" style={{ fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px', textDecoration: 'none', color: 'inherit' }}>
                {user.username}
                {user.role === 'admin' && (
                  <span title="Onaylı Hesap" style={{ color: '#007bff', display: 'inline-flex' }}>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" stroke="white" strokeWidth="1" />
                    </svg>
                  </span>
                )}
              </a>
            </div>
          ) : <a href="login.php" style={{fontSize: '14px', textDecoration: 'none', color: '#d32f2f', fontWeight: 'bold'}}>Giriş</a>}
        </div>
      </header>

      {/* Arama Çubuğu */}
      <div className="search-bar-container">
        <form onSubmit={handleSearch} className="search-form">
          <Search size={18} className="search-icon" />
          <input 
            type="text" 
            placeholder="Kelime, ilan no ile ara..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && <X size={18} className="clear-icon" onClick={() => setSearchQuery('')} />}
        </form>
      </div>

      <main className="category-view">
        {viewMode === 'vitrin' ? (
          <div className="showcase-section" style={{marginTop: '10px'}}>
            {/* Şirket Hesapları Butonu */}
            <div style={{padding: '0 12px 15px', marginBottom: '15px'}}>
              <button 
                onClick={() => window.location.href = 'company_list.php'}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(211, 47, 47, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(211, 47, 47, 0.3)';
                }}
              >
                <Building size={20} />
                <span>Şirket Hesapları</span>
              </button>
            </div>
            
            <div className="section-header">
              <Clock size={18} />
              <span>Son Eklenen İlanlar</span>
            </div>
            <div className="horizontal-scroll">
              {latestListings.map(l => (
                    <div key={l.id} className="scroll-item" onClick={() => window.location.href = `listing_detail.php?id=${l.id}`}>
                      <div className="scroll-image" style={{position: 'relative'}}>
                        {l.video_path ? (
                          <video
                            src={l.video_path}
                            muted
                            playsInline
                            autoPlay
                            loop={false}
                            preload="metadata"
                            onTimeUpdate={(e) => {
                              const v = e.target;
                              if (v.currentTime >= 5) v.currentTime = 0;
                            }}
                            className="scroll-video"
                          />
                        ) : l.main_image ? (
                          <img src={l.main_image} alt={l.title} loading="lazy" decoding="async" />
                        ) : (
                          <ImageIcon size={24} color="#ccc" />
                        )}
                        {l.is_featured === "1" && (
                          <div style={{position: 'absolute', top: 5, right: 5, background: '#ff9800', color: 'white', fontSize: '8px', padding: '2px 4px', borderRadius: '4px', fontWeight: 'bold'}}>ÖNE ÇIKAN</div>
                        )}
                      </div>
                      <div className="scroll-title">{l.title}</div>
                      <div className="scroll-price">{l.price ? `${new Intl.NumberFormat('tr-TR').format(l.price)} ${l.currency}` : 'Fiyat Belirtilmedi'}</div>
                      <div className="scroll-stats">
                        <span><Eye size={14} /> {l.views ?? 0}</span>
                        <span><Heart size={14} /> {l.favorite_count ?? 0}</span>
                      </div>
                    </div>
              ))}
            </div>
            {latestListings.length === 0 && <p style={{textAlign: 'center', color: '#999', marginTop: '20px'}}>Henüz ilan bulunmuyor.</p>}
          </div>
        ) : viewMode === 'categories' ? (
          <>
            {/* Filtreleme esnasında her alt kategoriye geçilince: yol + Tüm X ilanları */}
            {pathCategories.length > 0 && (
              <div className="listings-header-block filter-bar-sticky">
                <div className="filter-breadcrumb" style={{
                  background: '#f5f5f5',
                  borderBottom: '1px solid #e8e8e8',
                  padding: '12px 15px',
                  fontSize: '14px',
                  color: '#444'
                }}>
                  <span style={{ marginRight: '6px' }}>🏠</span>
                  <span>İlanlar</span>
                  {pathCategories.map((cat, i) => (
                    <span key={cat?.id || i}>
                      <span style={{ margin: '0 6px' }}>&gt;</span>
                      <span>{cat?.name}</span>
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => selectedCategory && fetchListings(selectedCategory.id)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 15px',
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#1a237e',
                    textDecoration: 'none',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  Tüm &apos;{pathCategories[pathCategories.length - 1]?.name}&apos; ilanları ({selectedCategoryCount})
                  <span style={{ marginLeft: '6px', fontSize: '18px' }}>&gt;</span>
                </button>
              </div>
            )}
            <div className="category-list">
            {currentLayer.map(cat => (
              <div 
                key={cat.id} 
                className="category-item"
                onPointerEnter={() => prefetchCategoryLayerOnHover(cat.id)}
                onClick={() => handleCategoryClick(cat)}
              >
                <div className="cat-info">
                  <div className="cat-icon-bg">
                    {renderCategoryLeftIcon(cat.name)}
                  </div>
                  <span className="cat-name">{cat.name}</span>
                </div>
                <div className="cat-meta">
                  <span className="cat-count">{cat.count}</span>
                  <ChevronRight size={18} color="#ccc" />
                </div>
              </div>
            ))}
            {currentLayer.length === 0 && !categoriesBusy && (
              <p style={{ textAlign: 'center', color: '#888', padding: '24px 16px', fontSize: 14 }}>
                Kategori listesi boş. Veritabanında kök kategori yoksa veya API hata veriyorsa tarayıcı konsolunu (F12) kontrol edin.
              </p>
            )}
          </div>
          </>
        ) : (
          <>
            {/* İlan listesi görünümünde de aynı yol + Tüm X ilanları (sayı ile) */}
            {viewMode === 'listings' && pathCategories.length > 0 && (
              <div className="listings-header-block filter-bar-sticky">
                <div className="filter-breadcrumb" style={{
                  background: '#f5f5f5',
                  borderBottom: '1px solid #e8e8e8',
                  padding: '12px 15px',
                  fontSize: '14px',
                  color: '#444'
                }}>
                  <span style={{ marginRight: '6px' }}>🏠</span>
                  <span>İlanlar</span>
                  {pathCategories.map((cat, i) => (
                    <span key={cat?.id || i}>
                      <span style={{ margin: '0 6px' }}>&gt;</span>
                      <span>{cat?.name}</span>
                    </span>
                  ))}
                </div>
                <a
                  href="#listings-grid"
                  className="tum-ilanlar-link"
                  style={{
                    display: 'block',
                    padding: '12px 15px',
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#1a237e',
                    textDecoration: 'none'
                  }}
                >
                  Tüm &apos;{selectedCategory?.name}&apos; ilanları ({listings.length})
                  <span style={{ marginLeft: '6px', fontSize: '18px' }}>&gt;</span>
                </a>
              </div>
            )}
            {viewMode === 'search' && (
              <div style={{ marginBottom: '16px', padding: '12px 15px' }}>
                <a
                  href="#listings-grid"
                  style={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#1a237e',
                    textDecoration: 'none'
                  }}
                >
                  Tüm arama sonuçları ({listings.length})
                  <span style={{ marginLeft: '6px', fontSize: '18px' }}>&gt;</span>
                </a>
              </div>
            )}
            <div
              key={listingsRenderKey}
              id="listings-grid"
              className={`listings-grid listings-grid-enter${listingsBusy ? ' listings-grid--busy' : ''}`}
            >
            {listings.length > 0 ? listings.map(l => (
              <div key={l.id} className="listing-card-modern" onClick={() => window.location.href = `listing_detail.php?id=${l.id}`}>
                <div className="listing-image-container">
                  {l.main_image ? (
                    <img src={l.main_image} alt={l.title} loading="lazy" decoding="async" />
                  ) : (
                    <div className="no-image"><ImageIcon size={48} color="#ccc" /></div>
                  )}
                  {l.is_featured === "1" && (
                    <div className="featured-badge">Öne Çıkan ⭐</div>
                  )}
                  <div className="listing-price-tag">
                    {l.price ? `${new Intl.NumberFormat('tr-TR').format(l.price)} ${l.currency}` : 'Fiyat Belirtilmedi'}
                  </div>
                </div>
                <div className="listing-content">
                  <h3 className="listing-title">{l.title}</h3>
                  <div className="listing-stats">
                    <span><Eye size={14} /> {l.views ?? 0}</span>
                    <span><Heart size={14} /> {l.favorite_count ?? 0}</span>
                  </div>
                  <div className="listing-bottom">
                    <span className="listing-location" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {l.username}
                      {l.user_role === 'admin' && (
                        <span style={{ color: '#007bff' }}>
                          <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" stroke="white" strokeWidth="1" />
                          </svg>
                        </span>
                      )}
                    </span>
                    <span className="listing-date">{new Date(l.created_at).toLocaleDateString('tr-TR')}</span>
                  </div>
                </div>
              </div>
            )) : (
              <div style={{textAlign: 'center', padding: '50px 20px', color: '#999'}}>
                <ImageIcon size={64} style={{marginBottom: '15px', opacity: 0.5}} />
                <p>{viewMode === 'search' ? 'Aradığınız kriterlere uygun ilan bulunamadı.' : 'Bu kategoride henüz onaylanmış ilan bulunmuyor.'}</p>
              </div>
            )}
          </div>
          </>
        )}
      </main>

      <nav className="bottom-nav">
        <button type="button" className={`nav-item ${viewMode === 'vitrin' ? 'active' : ''}`} onClick={() => {
          categoryFetchCtrlRef.current?.abort();
          listingsFetchCtrlRef.current?.abort();
          setCategoriesBusy(false);
          setListingsBusy(false);
          if (navStackDepthRef.current > 0) {
            suppressPopstateRef.current = true;
            window.history.go(-navStackDepthRef.current);
            navStackDepthRef.current = 0;
            setNavStackDepth(0);
          }
          setViewMode('vitrin');
          setHistory([]);
          setSelectedCategory(null);
          setSelectedCategoryCount(0);
          preSearchRef.current = null;
        }}>
          <Home size={22} />
          <span>Vitrin</span>
        </button>
        <button type="button" className={`nav-item ${viewMode === 'categories' || viewMode === 'listings' ? 'active' : ''}`} onClick={() => {
          listingsFetchCtrlRef.current?.abort();
          setListingsBusy(false);
          categoryLayerCacheRef.current.clear();
          prefetchQueueRef.current.length = 0;
          if (viewMode === 'vitrin') {
            pushNavHistory();
          }
          setViewMode('categories');
          fetchCategories(null);
          setHistory([]);
          setSelectedCategory(null);
          setSelectedCategoryCount(0);
          preSearchRef.current = null;
        }}>
          <SlidersHorizontal size={22} />
          <span>Filtrele</span>
        </button>
        <button className="nav-item special" onClick={() => window.location.href = 'add_listing.php'}>
          <div className="nav-icon-bg">
            <PlusCircle size={24} />
          </div>
          <span>İlan Ver</span>
        </button>
        <button className="nav-item" onClick={() => window.location.href = 'messages.php'} style={{ position: 'relative' }}>
          <MessageCircle size={22} />
          <span>Mesajlar</span>
          {hasUnreadMessages && <span className="message-dot"></span>}
        </button>
        <button className="nav-item" onClick={() => window.location.href = 'panel.php'}>
          <User size={22} />
          <span>Hesabım</span>
        </button>
      </nav>
    </div>
  );
}

export default App;
